import React, { useEffect, useState } from "react";
import { Plus, GraduationCap, BookOpen, Award, Clock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, EmptyState } from "../components/ui";

const PROGRESS_STEPS = [0, 25, 50, 75, 100];

export default function Learning() {
  const { user } = useAuth();
  const [tab, setTab] = useState("my");
  const [data, setData] = useState(null);
  const [my, setMy] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Technical", duration: "", hours: "", description: "", mandatory: false });
  const canManage = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/learning").then(({ data }) => setData(data));
  const [paths, setPaths] = useState([]);
  const loadPaths = () => api.get("/learning/paths").then(({ data }) => setPaths(data)).catch(() => setPaths([]));
  const loadMy = () => api.get("/learning/my").then(({ data }) => setMy(data)).catch(() => setMy({ enrollments: [], total: 0, completed: 0, hoursLogged: 0 }));
  useEffect(() => { load(); loadMy(); loadPaths(); }, []);
  const enroll = async (id) => { try { await api.post("/learning/enroll", { courseId: id }); load(); loadMy(); } catch (e) { window.alert(apiError(e.response?.data?.detail)); } };
  const enrollPath = async (id) => { try { await api.post(`/learning/paths/${id}/enroll`); loadPaths(); loadMy(); load(); } catch (e) { window.alert(apiError(e.response?.data?.detail)); } };
  const setProgress = async (enrId, progress) => {
    try { await api.put(`/learning/enrollments/${enrId}/progress`, { progress: +progress }); loadMy(); load(); }
    catch (e) { window.alert(apiError(e.response?.data?.detail)); }
  };
  const addCourse = async (e) => {
    e.preventDefault();
    await api.post("/learning/courses/full", { ...form, hours: form.hours || 0 });
    setOpen(false); setForm({ title: "", category: "Technical", duration: "", hours: "", description: "", mandatory: false }); load();
  };
  if (!data || !my) return <Loading />;

  const myCourseIds = new Set(my.enrollments.map((e) => e.courseId));
  return (
    <div>
      <PageHeader title="Learning" subtitle="Courses, enrollments & development">
        {canManage && <button data-testid="add-course" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Add Course</button>}
      </PageHeader>
      <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {[["my", "My Learning"], ["catalog", "Course Catalog"], ["paths", "Learning Paths"], ["enrollments", "All Enrollments"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>

      {tab === "my" && (
        <div data-testid="my-learning">
          <div className="mb-6 grid grid-cols-3 gap-4">
            {[["Enrolled", my.total, BookOpen, "text-blue-600"], ["Completed", my.completed, Award, "text-emerald-600"], ["Hours Logged", my.hoursLogged, Clock, "text-amber-600"]].map(([k, v, Icon, tone]) => (
              <Card key={k} className="p-5"><div className="flex items-center justify-between"><span className="text-sm font-medium text-slate-500 dark:text-slate-400">{k}</span><Icon size={18} className={tone} /></div><p className="mt-2 font-head text-3xl font-extrabold text-slate-900 dark:text-white">{v}</p></Card>
            ))}
          </div>
          {my.enrollments.length === 0 ? <EmptyState icon={GraduationCap} title="You're not enrolled yet" hint="Browse the Course Catalog to enroll." /> : (
            <div className="space-y-3">
              {my.enrollments.map((e) => (
                <Card key={e.id} className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center" data-testid={`my-enrollment-${e.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2"><p className="font-semibold text-slate-800 dark:text-slate-100">{e.courseTitle}</p><Badge tone="info">{e.category}</Badge></div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="h-1.5 w-40 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-accent transition-all" style={{ width: `${e.progress}%` }} /></div>
                      <span className="font-mono text-xs text-slate-500">{e.progress}%</span>
                      <Badge value={e.status} />
                    </div>
                    {e.certificateNo && <p className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Award size={13} /> {e.certificateNo}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-400">Update progress</label>
                    <select data-testid={`progress-${e.id}`} value={e.progress} onChange={(ev) => setProgress(e.id, ev.target.value)} className="input h-9 w-24 py-0">
                      {PROGRESS_STEPS.map((p) => <option key={p} value={p}>{p}%</option>)}
                    </select>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "catalog" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.courses.map((c) => (
            <Card key={c.id} className="flex flex-col p-6" data-testid={`course-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent/10 text-accent"><BookOpen size={20} /></div>
                {c.mandatory && <span className="rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-600 dark:bg-red-500/15">Mandatory</span>}
              </div>
              <h3 className="mt-3 font-head text-base font-bold text-slate-900 dark:text-white">{c.title}</h3>
              <p className="mt-1 flex-1 text-sm text-slate-500 dark:text-slate-400">{c.description}</p>
              <div className="mt-3 flex items-center justify-between">
                <Badge tone="info">{c.category}</Badge>
                <span className="text-xs text-slate-400">{c.hours ? `${c.hours}h` : c.duration} · {c.enrolledCount} enrolled</span>
              </div>
              <button data-testid={`enroll-${c.id}`} disabled={myCourseIds.has(c.id)} onClick={() => enroll(c.id)} className="btn-outline mt-4 w-full disabled:opacity-50">{myCourseIds.has(c.id) ? "Enrolled" : "Enroll"}</button>
            </Card>
          ))}
        </div>
      )}

      {tab === "paths" && (
        paths.length === 0 ? <EmptyState icon={GraduationCap} title="No learning paths" hint="HR can group courses into guided paths." /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="paths">
          {paths.map((p) => (
            <Card key={p.id} className="flex flex-col p-6" data-testid={`path-${p.id}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">{p.title}</h3>
                {p.certificateNo && <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-500/15"><Award size={11} /> {p.certificateNo}</span>}
              </div>
              {p.description && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{p.description}</p>}
              <div className="mt-3 space-y-1">{p.courses.map((c, i) => <p key={c.id} className="text-sm text-slate-600 dark:text-slate-300">{i + 1}. {c.title}</p>)}</div>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-accent" style={{ width: `${p.progress}%` }} /></div>
                <span className="font-mono text-xs text-slate-500">{p.completedCourses}/{p.totalCourses}</span>
              </div>
              <button data-testid={`path-enroll-${p.id}`} disabled={p.enrolled} onClick={() => enrollPath(p.id)} className="btn-outline mt-4 w-full disabled:opacity-50">{p.enrolled ? "Enrolled" : "Start Path"}</button>
            </Card>
          ))}
        </div>
      ))}

      {tab === "enrollments" && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full" data-testid="enrollments-table">
            <thead><tr><th className="th">Employee</th><th className="th">Course</th><th className="th">Progress</th><th className="th">Status</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.enrollments.map((e) => (
                <tr key={e.id}>
                  <td className="td font-medium text-slate-800 dark:text-slate-100">{e.employeeName}</td>
                  <td className="td">{e.courseTitle}</td>
                  <td className="td"><div className="flex items-center gap-2"><div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-accent" style={{ width: `${e.progress}%` }} /></div><span className="font-mono text-xs">{e.progress}%</span></div></td>
                  <td className="td"><Badge value={e.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add Course"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="cr-form" className="btn-accent">Add</button></>}>
        <form id="cr-form" onSubmit={addCourse} className="space-y-4">
          <div><label className="label">Title *</label><input required data-testid="cr-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Category</label><input data-testid="cr-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" /></div>
            <div><label className="label">Hours</label><input type="number" step="0.5" data-testid="cr-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="input" placeholder="e.g. 6" /></div>
          </div>
          <div><label className="label">Description</label><textarea data-testid="cr-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input h-20 py-2" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"><input type="checkbox" data-testid="cr-mandatory" checked={form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} /> Mandatory course</label>
        </form>
      </Modal>
    </div>
  );
}
